import { useEffect, useState, type DragEvent } from 'react';
import { api, download, openDocument } from '../../lib/api';
import { hasRole } from '../../lib/auth';
import { T, useGym } from '../../lib/gym';
import { useApi, useInterval } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { Link, navigate, setQuery, useLocation } from '../../lib/router';
import type { TokenBalance } from '../../lib/types';
import {
  AsyncButton, Badge, Button, Card, Checkbox, cx, DataTable, Empty, ErrorBox, Field, Input, Loading, Modal, PageHeader, Select, Tabs, Textarea, useToast,
} from '../../ui/components';
import { ResourcePage, SchemaForm, useMeta, useOptions } from '../../ui/schema';
import { TokenChips } from '../member/planner';
import { AddLeadModal } from './dashboard';
import { kg } from '../member/training';
import { CoachingTab } from './coaching';
import { MeasurementsTab } from './measurements';

export function ClientsPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const { query } = useLocation();
  const stage = query.get('stage') ?? '';
  const [lead, setLead] = useState(false);
  const [importing, setImporting] = useState(false);
  const [csv, setCsv] = useState('');
  const [key, setKey] = useState(0);
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.clients')} />
      <div className="row gap-sm wrap">
        {['', 'lead', 'trial', 'member', 'former_member'].map((s) => (
          <button key={s} type="button" className={cx('chip', stage === s && 'active')} onClick={() => setQuery({ stage: s || null })}>{s ? i18n.option(s) : i18n.t('common.all')}</button>
        ))}
      </div>
      <ResourcePage key={`${stage}-${key}`} slug={gym.slug} resource="persons" query={stage ? { stage } : {}}
        columns={['first_name', 'last_name', 'email', 'phone', 'stage', 'last_visit_at']}
        onRowClick={(r) => navigate(`${gym.base}/admin/clients/${r.id}`)}
        actions={<>
          <Button variant="secondary" onClick={() => setLead(true)}>+ {i18n.t('crm.add_lead')}</Button>
          {hasRole(gym.membership, 'owner', 'manager') && <Button variant="ghost" onClick={() => setImporting(true)}>{i18n.t('crm.import')}</Button>}
        </>} />
      {lead && <AddLeadModal onClose={() => setLead(false)} onSaved={(id) => (id ? navigate(`${gym.base}/admin/clients/${id}`) : setKey((k) => k + 1))} />}
      {importing && (
        <Modal wide title={i18n.t('crm.import')} onClose={() => setImporting(false)} footer={
          <AsyncButton disabled={!csv.trim()} onClick={async () => {
            const r = await api(`${T(gym.slug)}/admin/persons/import`, { body: { csv } });
            toast.ok(i18n.t('crm.imported', { created: r.created ?? 0, updated: r.updated ?? 0, failed: r.errors?.length ?? 0 }));
            setImporting(false);
            setKey((k) => k + 1);
          }}>{i18n.t('crm.import')}</AsyncButton>
        }>
          <p className="small muted">{i18n.t('crm.import_help')}</p>
          <input type="file" accept=".csv,text/csv" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setCsv(await f.text()); }} />
          <Textarea className="input mono" rows={10} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder="first_name,last_name,email,phone,stage" />
        </Modal>
      )}
    </div>
  );
}

export function ClientDetailPage({ id }: { id: string }) {
  const gym = useGym();
  const i18n = useI18n();
  const { query } = useLocation();
  const tab = query.get('tab') ?? 'overview';
  const o = useApi(() => api(`${T(gym.slug)}/admin/persons/${id}/overview`), [id]);
  if (o.error) return <div className="page"><ErrorBox error={o.error} onRetry={o.reload} /></div>;
  if (!o.data) return <Loading />;
  const p = o.data.person;
  const mgmt = hasRole(gym.membership, 'owner', 'manager');
  return (
    <div className="page">
      <Link to={`${gym.base}/admin/clients`} className="small">← {i18n.t('nav.clients')}</Link>
      <PageHeader
        title={<>{p.first_name} {p.last_name === '-' ? '' : p.last_name} <Badge value={p.stage} /> {o.data.health.attention && <span className="badge badge-bad">⚕</span>}</>}
        subtitle={[p.email, p.phone, p.member_number && `#${p.member_number}`].filter(Boolean).join(' · ')}
        actions={<>
          {p.phone && <a className="btn btn-secondary" href={`tel:${p.phone}`}>{i18n.t('crm.call')}</a>}
          {p.email && <a className="btn btn-secondary" href={`mailto:${p.email}`}>{i18n.t('crm.email')}</a>}
        </>}
      />
      {o.data.outstandingCents > 0 && <div className="alert alert-warn">{i18n.t('crm.outstanding', { amount: i18n.money(o.data.outstandingCents) })}</div>}
      <Tabs value={tab} onChange={(v) => setQuery({ tab: v })} tabs={[
        { id: 'overview', label: i18n.t('crm.tab_overview') },
        { id: 'bookings', label: i18n.t('nav.bookings') },
        { id: 'payments', label: i18n.t('profile.tab_payments') },
        { id: 'notes', label: i18n.t('crm.tab_notes') },
        { id: 'documents', label: i18n.t('profile.tab_documents') },
        { id: 'health', label: i18n.t('profile.tab_health'), hidden: !o.data.health.canView },
        { id: 'training', label: i18n.t('nav.training') },
        { id: 'coaching', label: i18n.t('coaching.title') },
        { id: 'body', label: i18n.t('body.title') },
        { id: 'details', label: i18n.t('profile.tab_details') },
        { id: 'privacy', label: i18n.t('profile.tab_privacy'), hidden: !mgmt },
      ]} />
      {tab === 'overview' && <OverviewTab id={id} data={o.data} reload={o.reload} />}
      {tab === 'bookings' && (
        <DataTable rows={o.data.bookings} columns={[
          { key: 'starts_at', label: i18n.t('planner.when'), render: (b) => i18n.dateTime(b.starts_at) },
          { key: 'lesson_name', label: i18n.t('planner.lesson') },
          { key: 'location_name', label: i18n.t('planner.where') },
          { key: 'status', label: i18n.field('status'), render: (b) => <>{<Badge value={b.status} />} {b.is_trial && <span className="badge badge-warn">{i18n.t('attendance.trial')}</span>}</> },
          { key: 'entitlement', label: i18n.field('entitlement'), render: (b) => i18n.option(b.entitlement ?? '—') },
          { key: 'x', label: '', render: (b) => (['booked', 'waitlisted'].includes(b.status) && b.starts_at > new Date().toISOString()
            ? <AsyncButton size="sm" variant="ghost" onClick={async () => { await api(`${T(gym.slug)}/admin/bookings/${b.id}/cancel`, { body: {} }); o.reload(); }}>{i18n.t('common.cancel')}</AsyncButton> : null) },
        ]} empty={i18n.t('bookings.none')} />
      )}
      {tab === 'payments' && <PaymentsAdminTab data={o.data} reload={o.reload} />}
      {tab === 'notes' && <NotesTab id={id} activities={o.data.activities} reload={o.reload} />}
      {tab === 'documents' && <DocumentsAdminTab id={id} contracts={o.data.contracts} reload={o.reload} />}
      {tab === 'health' && <HealthAdminTab id={id} />}
      {tab === 'training' && <TrainingAdminTab personId={id} />}
      {tab === 'coaching' && <CoachingTab personId={id} />}
      {tab === 'body' && <MeasurementsTab personId={id} />}
      {tab === 'details' && <DetailsAdminTab person={p} reload={o.reload} />}
      {tab === 'privacy' && <PrivacyAdminTab id={id} data={o.data} reload={o.reload} />}
    </div>
  );
}

function OverviewTab({ id, data, reload }: { id: string; data: any; reload(): void }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const plans = useOptions(gym.slug, 'membership_plans');
  const tokenTypes = useOptions(gym.slug, 'token_types');
  const tokens = useApi(() => api<TokenBalance[]>(`${T(gym.slug)}/admin/persons/${id}/tokens`), [id]);
  const [sell, setSell] = useState({ planId: '', startDate: '', early: false });
  const [link, setLink] = useState<string | null>(null);
  const [adj, setAdj] = useState({ tokenTypeId: '', quantity: 1, note: '', mode: 'grant' });
  const [pause, setPause] = useState<any | null>(null);
  const [dates, setDates] = useState({ startDate: '', endDate: '' });
  const mgmt = hasRole(gym.membership, 'owner', 'manager');
  const act = async (m: any, action: string, body: Record<string, unknown> = {}): Promise<void> => {
    const r = await api(`${T(gym.slug)}/admin/memberships/${m.id}/${action}`, { body });
    toast.ok(action === 'cancel' && r.effectiveDate ? i18n.t('profile.cancel_done', { date: i18n.day(r.effectiveDate) }) : i18n.t('common.saved'));
    reload();
  };
  return (
    <div className="grid-2 align-start">
      <div className="stack">
        <Card title={i18n.t('crm.memberships')}>
          {data.memberships.length === 0 && <p className="muted">{i18n.t('profile.no_membership')}</p>}
          {data.memberships.map((m: any) => (
            <div key={m.id} className="membership-row">
              <div>
                <strong>{m.plan_name}</strong> <Badge value={m.status} />
                <div className="small muted">
                  {i18n.money(m.price_cents)} · {m.start_date ? i18n.day(m.start_date) : '—'}{m.cancel_effective_date ? ` → ${i18n.day(m.cancel_effective_date)}` : ''}{m.dunning_stage && m.dunning_stage !== 'none' ? ` · ${i18n.option(m.dunning_stage)}` : ''}
                </div>
              </div>
              <div className="row gap-sm wrap">
                {['active', 'paused', 'suspended'].includes(m.status) && <AsyncButton size="sm" variant="secondary" confirm={i18n.t('crm.cancel_confirm')} onClick={() => act(m, 'cancel', { reason: 'staff' })}>{i18n.t('common.cancel')}</AsyncButton>}
                {m.status === 'active' && <Button size="sm" variant="ghost" onClick={() => setPause(m)}>{i18n.t('profile.pause')}</Button>}
                {['paused', 'suspended'].includes(m.status) && <AsyncButton size="sm" variant="ghost" onClick={() => act(m, 'resume')}>{i18n.t('crm.resume')}</AsyncButton>}
                {hasRole(gym.membership, 'owner', 'manager', 'finance') && !['withdrawn', 'cancelled', 'ended'].includes(m.status) && (
                  <AsyncButton size="sm" variant="ghost" confirm={i18n.t('profile.withdraw_confirm')} onClick={() => act(m, 'withdraw')}>{i18n.t('crm.withdraw')}</AsyncButton>
                )}
              </div>
            </div>
          ))}
          <details className="details">
            <summary>{i18n.t('crm.sell_membership')}</summary>
            <div className="grid-2">
              <Field label={i18n.field('plan_id')}><Select value={sell.planId} placeholder="—" onChange={(e) => setSell({ ...sell, planId: e.target.value })} options={plans.map((p) => ({ value: p.id, label: p.label }))} /></Field>
              <Field label={i18n.field('start_date')}><Input type="date" value={sell.startDate} onChange={(e) => setSell({ ...sell, startDate: e.target.value })} /></Field>
            </div>
            <Checkbox checked={sell.early} onChange={(c) => setSell({ ...sell, early: c })} label={i18n.t('crm.early_start')} />
            <AsyncButton disabled={!sell.planId} onClick={async () => {
              const r = await api(`${T(gym.slug)}/admin/memberships`, { body: { personId: id, planId: sell.planId, startDate: sell.startDate || null, earlyStartConsent: sell.early } });
              setLink(r.checkoutUrl);
              toast.ok(i18n.t(r.activated ? 'crm.membership_active' : 'crm.payment_link_sent'));
              reload();
            }}>{i18n.t('crm.create_membership')}</AsyncButton>
            {link && <p className="small">{i18n.t('crm.payment_link')}: <a href={link} target="_blank" rel="noreferrer">{link.slice(0, 60)}…</a></p>}
          </details>
        </Card>
        <Card title={i18n.t('profile.tokens')}>
          <TokenChips tokens={tokens.data} />
          {mgmt && (
            <details className="details">
              <summary>{i18n.t('crm.adjust_tokens')}</summary>
              <div className="grid-2">
                <Field label={i18n.t('planner.token')}><Select value={adj.tokenTypeId} placeholder="—" onChange={(e) => setAdj({ ...adj, tokenTypeId: e.target.value })} options={tokenTypes.map((t) => ({ value: t.id, label: t.label }))} /></Field>
                <Field label={i18n.t('crm.action')}><Select value={adj.mode} onChange={(e) => setAdj({ ...adj, mode: e.target.value })} options={['grant', 'compensation', 'deduct'].map((m) => ({ value: m, label: i18n.t(`crm.token_${m}`) }))} /></Field>
                <Field label={i18n.field('quantity')}><Input type="number" min={1} value={adj.quantity} onChange={(e) => setAdj({ ...adj, quantity: Number(e.target.value) })} /></Field>
                <Field label={i18n.field('note')}><Input value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })} /></Field>
              </div>
              <AsyncButton disabled={!adj.tokenTypeId || adj.quantity < 1 || (adj.mode === 'deduct' && !adj.note)} onClick={async () => {
                if (adj.mode === 'deduct') await api(`${T(gym.slug)}/admin/persons/${id}/tokens/adjust`, { body: { tokenTypeId: adj.tokenTypeId, delta: -adj.quantity, note: adj.note } });
                else await api(`${T(gym.slug)}/admin/persons/${id}/tokens`, { body: { tokenTypeId: adj.tokenTypeId, quantity: adj.quantity, note: adj.note, source: adj.mode === 'compensation' ? 'compensation' : 'admin' } });
                toast.ok(i18n.t('common.saved'));
                tokens.reload();
              }}>{i18n.t('common.save')}</AsyncButton>
            </details>
          )}
        </Card>
      </div>
      <div className="stack">
        <Card title={i18n.t('crm.sales')}>
          {data.opportunities.length === 0 ? <p className="muted">—</p> : data.opportunities.map((op: any) => (
            <div key={op.id} className="row between">
              <span><Badge value={op.stage} /> {op.source && <span className="small muted">{i18n.option(op.source)}{op.campaign ? ` · ${op.campaign}` : ''}</span>}</span>
              <span className="small muted">{i18n.date(op.created_at)}</span>
            </div>
          ))}
        </Card>
        <Card title={i18n.t('nav.tasks')}>
          {data.tasks.length === 0 ? <p className="muted">—</p> : data.tasks.map((t: any) => (
            <div key={t.id} className="row between">
              <span>{t.title}<br /><small className="muted">{t.due_at ? i18n.dateTime(t.due_at) : ''}</small></span>
              <AsyncButton size="sm" variant="secondary" onClick={async () => { await api(`${T(gym.slug)}/admin/r/tasks/${t.id}`, { method: 'PATCH', body: { status: 'done' } }); reload(); }}>✓</AsyncButton>
            </div>
          ))}
        </Card>
        <Card title={i18n.t('crm.stats')}>
          <dl className="dl">
            <div><dt>{i18n.field('visits_total')}</dt><dd>{data.person.visits_total ?? 0}</dd></div>
            <div><dt>{i18n.field('last_visit_at')}</dt><dd>{data.person.last_visit_at ? i18n.date(data.person.last_visit_at) : '—'}</dd></div>
            <div><dt>{i18n.field('no_show_count')}</dt><dd className={data.person.no_show_count >= 3 ? 'warn' : ''}>{data.person.no_show_count ?? 0}</dd></div>
            <div><dt>{i18n.field('late_cancel_count')}</dt><dd>{data.person.late_cancel_count ?? 0}</dd></div>
            <div><dt>{i18n.field('lead_source')}</dt><dd>{data.person.lead_source ? i18n.option(data.person.lead_source) : '—'}</dd></div>
            <div><dt>{i18n.t('crm.app_account')}</dt><dd>{data.account ? `${data.account.login_email}` : '—'}</dd></div>
          </dl>
        </Card>
      </div>
      {pause && (
        <Modal title={i18n.t('profile.pause')} onClose={() => setPause(null)} footer={<AsyncButton onClick={async () => { await act(pause, 'pause', dates); setPause(null); }}>{i18n.t('common.save')}</AsyncButton>}>
          <div className="grid-2">
            <Field label={i18n.field('start_date')}><Input type="date" value={dates.startDate} onChange={(e) => setDates({ ...dates, startDate: e.target.value })} /></Field>
            <Field label={i18n.field('end_date')}><Input type="date" value={dates.endDate} onChange={(e) => setDates({ ...dates, endDate: e.target.value })} /></Field>
          </div>
        </Modal>
      )}
    </div>
  );
}

function PaymentsAdminTab({ data, reload }: { data: any; reload(): void }) {
  const gym = useGym();
  const i18n = useI18n();
  const canRefund = hasRole(gym.membership, 'owner', 'finance');
  return (
    <div className="stack">
      <Card title={i18n.t('profile.payments')}>
        <DataTable rows={data.payments} columns={[
          { key: 'created_at', label: i18n.field('created_at'), render: (r) => i18n.dateTime(r.paid_at ?? r.created_at) },
          { key: 'kind', label: i18n.field('kind'), render: (r) => i18n.option(r.kind) },
          { key: 'method', label: i18n.field('method'), render: (r) => (r.method ? i18n.option(r.method) : '—') },
          { key: 'amount', label: i18n.field('amount_cents'), render: (r) => i18n.money(r.amount_cents, r.currency) },
          { key: 'status', label: i18n.field('status'), render: (r) => <><Badge value={r.status} />{r.failure_message && <div className="small warn">{r.failure_message}</div>}</> },
          { key: 'x', label: '', render: (r) => (canRefund && ['paid', 'partially_refunded'].includes(r.status) ? (
            <AsyncButton size="sm" variant="ghost" onClick={async () => {
              const input = window.prompt(i18n.t('crm.refund_prompt'), String((r.amount_cents - (r.refunded_cents ?? 0)) / 100));
              if (input === null) return;
              await api(`${T(gym.slug)}/admin/payments/${r.id}/refund`, { body: { amountCents: Math.round(Number(input.replace(',', '.')) * 100) } });
              reload();
            }}>{i18n.t('crm.refund')}</AsyncButton>
          ) : null) },
        ]} empty={i18n.t('profile.no_payments')} />
      </Card>
      <Card title={i18n.t('profile.invoices')}>
        <DataTable rows={data.invoices} columns={[
          { key: 'number', label: i18n.field('number') },
          { key: 'due_date', label: i18n.field('due_date'), render: (r) => (r.due_date ? i18n.day(r.due_date) : '—') },
          { key: 'amount', label: i18n.field('amount_due_cents'), render: (r) => i18n.money(r.amount_due_cents, r.currency) },
          { key: 'status', label: i18n.field('status'), render: (r) => <Badge value={r.status} /> },
          { key: 'link', label: '', render: (r) => (r.hosted_url ? <a href={r.hosted_url} target="_blank" rel="noreferrer">{i18n.t('common.open')}</a> : null) },
        ]} empty={i18n.t('profile.no_invoices')} />
      </Card>
    </div>
  );
}

function NotesTab({ id, activities, reload }: { id: string; activities: any[]; reload(): void }) {
  const gym = useGym();
  const i18n = useI18n();
  const [body, setBody] = useState('');
  const [type, setType] = useState('note');
  return (
    <div className="stack">
      <Card>
        <div className="row gap-sm">
          <Select value={type} onChange={(e) => setType(e.target.value)} options={['note', 'call', 'email', 'meeting', 'sms'].map((x) => ({ value: x, label: i18n.option(x) }))} />
          <Textarea rows={2} value={body} placeholder={i18n.t('crm.note_placeholder')} onChange={(e) => setBody(e.target.value)} />
          <AsyncButton disabled={!body.trim()} onClick={async () => { await api(`${T(gym.slug)}/admin/persons/${id}/notes`, { body: { body, activity_type: type } }); setBody(''); reload(); }}>{i18n.t('common.add')}</AsyncButton>
        </div>
      </Card>
      <div className="timeline">
        {activities.map((a) => (
          <div key={a.id} className="timeline-item">
            <div className="small muted">{i18n.dateTime(a.created_at)} · {i18n.option(a.activity_type)}</div>
            {a.title && <strong>{a.title}</strong>}
            {a.body && <p className="pre">{a.body}</p>}
          </div>
        ))}
        {activities.length === 0 && <Empty>{i18n.t('crm.no_activity')}</Empty>}
      </div>
    </div>
  );
}

export function ContractGenerator({ personId, onCreated, kind }: { personId?: string; onCreated(): void; kind?: 'client' | 'trainer' }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const templates = useApi(async () => (await api<{ items: any[] }>(`${T(gym.slug)}/admin/r/contract_templates`, { query: { pageSize: 200, is_active: 'true' } })).items, []);
  const [templateId, setTemplateId] = useState('');
  const [dates, setDates] = useState({ startDate: '', endDate: '' });
  const [custom, setCustom] = useState<Record<string, string>>({});
  const placeholders = useApi(templateId ? () => api<{ custom: string[] }>(`${T(gym.slug)}/admin/contract-templates/${templateId}/placeholders`) : null, [templateId]);
  const list = (templates.data ?? []).filter((t) => !kind || (kind === 'trainer' ? t.category === 'staff' : t.category === 'client'));
  return (
    <div className="stack-sm">
      <div className="grid-3">
        <Field label={i18n.field('template_id')}><Select value={templateId} placeholder="—" onChange={(e) => { setTemplateId(e.target.value); setCustom({}); }} options={list.map((t) => ({ value: t.id, label: `${t.name}${t.tenant_id ? '' : ` (${i18n.t('contracts.standard')})`}` }))} /></Field>
        <Field label={i18n.field('start_date')}><Input type="date" value={dates.startDate} onChange={(e) => setDates({ ...dates, startDate: e.target.value })} /></Field>
        <Field label={i18n.field('end_date')}><Input type="date" value={dates.endDate} onChange={(e) => setDates({ ...dates, endDate: e.target.value })} /></Field>
      </div>
      {(placeholders.data?.custom ?? []).length > 0 && (
        <div className="grid-3">
          {placeholders.data!.custom.map((k) => <Field key={k} label={k.replace(/^custom\./, '').replace(/_/g, ' ')}><Input value={custom[k.replace(/^custom\./, '')] ?? ''} onChange={(e) => setCustom({ ...custom, [k.replace(/^custom\./, '')]: e.target.value })} /></Field>)}
        </div>
      )}
      <AsyncButton disabled={!templateId || !personId} onClick={async () => {
        const c = await api(`${T(gym.slug)}/admin/contracts`, { body: { templateId, personId, ...dates, custom } });
        toast.ok(i18n.t('contracts.created'));
        if (window.confirm(i18n.t('contracts.send_now'))) await api(`${T(gym.slug)}/admin/contracts/${c.id}/send`, { body: {} });
        onCreated();
      }}>{i18n.t('contracts.generate')}</AsyncButton>
    </div>
  );
}

export function ContractsTable({ rows, reload }: { rows: any[]; reload(): void }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  return (
    <DataTable rows={rows} columns={[
      { key: 'title', label: i18n.field('title') },
      { key: 'contract_type', label: i18n.field('contract_type'), render: (c) => i18n.option(c.contract_type) },
      { key: 'status', label: i18n.field('status'), render: (c) => <Badge value={c.status} /> },
      { key: 'signed_at', label: i18n.field('signed_at'), render: (c) => (c.signed_at ? i18n.date(c.signed_at) : '—') },
      { key: 'x', label: '', render: (c) => (
        <div className="row gap-sm end">
          <Button size="sm" variant="ghost" onClick={() => openDocument(`${T(gym.slug)}/admin/contracts/${c.id}/document`)}>{i18n.t('common.view')}</Button>
          {['draft', 'sent', 'viewed'].includes(c.status) && <AsyncButton size="sm" variant="secondary" onClick={async () => { await api(`${T(gym.slug)}/admin/contracts/${c.id}/send`, { body: {} }); toast.ok(i18n.t('contracts.sent')); reload(); }}>{i18n.t(c.status === 'draft' ? 'contracts.send' : 'contracts.resend')}</AsyncButton>}
          {c.status === 'signed' && c.requires_countersign !== false && !c.countersigned_at && hasRole(gym.membership, 'owner', 'manager') && (
            <AsyncButton size="sm" onClick={async () => { await api(`${T(gym.slug)}/admin/contracts/${c.id}/countersign`, { body: {} }); reload(); }}>{i18n.t('contracts.countersign')}</AsyncButton>
          )}
        </div>
      ) },
    ]} empty={i18n.t('profile.no_documents')} />
  );
}

function DocumentsAdminTab({ id, contracts, reload }: { id: string; contracts: any[]; reload(): void }) {
  const i18n = useI18n();
  return (
    <div className="stack">
      <Card title={i18n.t('profile.documents')}><ContractsTable rows={contracts} reload={reload} /></Card>
      {hasRole(useGym().membership, 'owner', 'manager') && <Card title={i18n.t('contracts.new')}><ContractGenerator personId={id} kind="client" onCreated={reload} /></Card>}
    </div>
  );
}

function HealthAdminTab({ id }: { id: string }) {
  const gym = useGym();
  const i18n = useI18n();
  const h = useApi(() => api(`${T(gym.slug)}/admin/persons/${id}/health`), [id]);
  if (h.error) return <ErrorBox error={h.error} />;
  if (!h.data) return <Loading />;
  const d = h.data;
  return (
    <div className="grid-2 align-start">
      <Card title={i18n.t('profile.health')}>
        <p className="small muted">{i18n.t('crm.health_audit')}</p>
        {!d.consentAt ? <p>{i18n.t('crm.no_health_consent')}</p> : (
          <>
            <ul className="ticks">
              {Object.entries(d.intake ?? {}).filter(([, v]) => v).map(([k]) => <li key={k}>{i18n.t(`health.q_${k}`)}</li>)}
            </ul>
            {d.notes && <p className="pre health-note">{d.notes}</p>}
            <p className="small">{i18n.t(d.shareWithTrainers ? 'crm.shared_with_trainers' : 'crm.not_shared_with_trainers')}</p>
            <p className="small muted">{i18n.t('crm.consent_given', { date: i18n.date(d.consentAt) })}</p>
          </>
        )}
      </Card>
      <Card title={i18n.t('profile.ice')}>
        {(d.emergencyContacts ?? []).length === 0 ? <p className="muted">—</p> : d.emergencyContacts.map((c: any, i: number) => (
          <p key={i}><strong>{c.name}</strong> ({c.relation}) <a href={`tel:${c.phone}`}>{c.phone}</a></p>
        ))}
      </Card>
    </div>
  );
}

/** Programme, logged workouts and progress of one client, for trainers and staff. */
function TrainingAdminTab({ personId }: { personId: string }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const program = useApi(() => api(`${T(gym.slug)}/admin/persons/${personId}/program`), [personId]);
  const workouts = useApi(() => api<any[]>(`${T(gym.slug)}/admin/persons/${personId}/workouts`), [personId]);
  const progress = useApi(() => api<any[]>(`${T(gym.slug)}/admin/persons/${personId}/progress`), [personId]);
  const templates = useApi(async () => (await api<{ items: any[] }>(`${T(gym.slug)}/admin/r/programs`, { query: { is_template: 'true', pageSize: 100 } })).items, []);
  const [templateId, setTemplateId] = useState('');
  return (
    <div className="stack">
      <Card title={i18n.t('training.program')} actions={program.data ? <Badge value={program.data.status} /> : null}>
        {program.data ? (
          <>
            <strong>{program.data.name}</strong>
            <ul className="sets">
              {(program.data.items ?? []).map((item: any) => (
                <li key={item.id}>{item.exercise}: {item.sets ?? 3} × {item.reps ?? '—'}{item.weight_grams ? ` @ ${kg(item.weight_grams)}` : ''}</li>
              ))}
            </ul>
          </>
        ) : <p className="muted">{i18n.t('training.no_program')}</p>}
        <div className="row gap-sm">
          <Select value={templateId} placeholder={i18n.t('training.choose_template')} onChange={(e) => setTemplateId(e.target.value)}
            options={(templates.data ?? []).map((t) => ({ value: t.id, label: t.name }))} />
          <AsyncButton disabled={!templateId} onClick={async () => {
            await api(`${T(gym.slug)}/admin/persons/${personId}/program`, { body: { templateId } });
            toast.ok(i18n.t('training.assigned'));
            program.reload();
          }}>{i18n.t('training.assign')}</AsyncButton>
        </div>
        <p className="small muted">{i18n.t('training.template_hint')}</p>
      </Card>
      <Card title={i18n.t('training.progress')}>
        {!progress.data ? <Loading /> : progress.data.length === 0 ? <p className="muted">{i18n.t('training.no_progress')}</p> : (
          <DataTable rows={progress.data.map((p) => ({ id: p.exerciseId, ...p }))} columns={[
            { key: 'exercise', label: i18n.t('training.exercise') },
            { key: 'best', label: i18n.t('training.best'), render: (r) => kg(r.bestGrams) },
            { key: 'sets', label: i18n.t('training.sets') },
            { key: 'lastAt', label: i18n.t('training.last'), render: (r) => i18n.date(r.lastAt) },
          ]} />
        )}
      </Card>
      <Card title={i18n.t('training.history')}>
        {!workouts.data ? <Loading /> : workouts.data.length === 0 ? <p className="muted">{i18n.t('training.no_workouts')}</p> : workouts.data.map((w) => (
          <div key={w.id} className="timeline-item">
            <div className="small muted">{i18n.dateTime(w.performed_at)}{w.feeling ? ` · ${i18n.option(w.feeling)}` : ''}</div>
            <ul className="sets">{(w.sets ?? []).map((s: any) => <li key={s.id}>{s.exercise}: {s.reps ?? '—'} × {kg(s.weight_grams)}</li>)}</ul>
          </div>
        ))}
      </Card>
    </div>
  );
}

function DetailsAdminTab({ person, reload }: { person: any; reload(): void }) {
  const gym = useGym();
  const meta = useMeta(gym.slug, 'persons');
  if (!meta) return <Loading />;
  return <Card><SchemaForm slug={gym.slug} meta={meta} row={person} hide={['emergency_contacts']} onSaved={reload} /></Card>;
}

function PrivacyAdminTab({ id, data, reload }: { id: string; data: any; reload(): void }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  return (
    <div className="stack">
      <Card title={i18n.t('crm.consents')}>
        <DataTable rows={data.consents} columns={[
          { key: 'created_at', label: i18n.field('created_at'), render: (c) => i18n.dateTime(c.created_at) },
          { key: 'consent_type', label: i18n.field('consent_type'), render: (c) => i18n.option(c.consent_type) },
          { key: 'granted', label: i18n.field('granted'), render: (c) => (c.granted ? '✓' : '✗') },
          { key: 'source', label: i18n.field('source') },
          { key: 'text_version', label: i18n.field('text_version') },
        ]} />
      </Card>
      <Card title={i18n.t('crm.app_access')}>
        {data.account ? <p>{i18n.t('crm.has_account', { email: data.account.login_email })}</p> : (
          <AsyncButton variant="secondary" disabled={!data.person.email} onClick={async () => { await api(`${T(gym.slug)}/admin/persons/${id}/invite`, { body: {} }); toast.ok(i18n.t('crm.invite_sent')); }}>{i18n.t('crm.invite')}</AsyncButton>
        )}
      </Card>
      <Card title={i18n.t('profile.your_data')} className="danger-zone">
        <div className="row gap-sm wrap">
          <AsyncButton variant="secondary" onClick={() => download(`${T(gym.slug)}/admin/persons/${id}/export`, `person-${id}.json`)}>{i18n.t('profile.export')}</AsyncButton>
          {hasRole(gym.membership, 'owner') && (
            <AsyncButton variant="danger" confirm={i18n.t('crm.anonymize_confirm')} onClick={async () => { await api(`${T(gym.slug)}/admin/persons/${id}/anonymize`, { body: {} }); toast.ok(i18n.t('crm.anonymized')); reload(); }}>{i18n.t('crm.anonymize')}</AsyncButton>
          )}
        </div>
      </Card>
    </div>
  );
}

export function PipelinePage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const [loc, setLoc] = useState('');
  const board = useApi(() => api(`${T(gym.slug)}/admin/pipeline`, { query: { location: loc } }), [loc]);
  const meta = useMeta(gym.slug, 'opportunities');
  const reasons = meta?.fields.find((f) => f.name === 'lost_reason')?.values ?? [];
  const [lost, setLost] = useState<{ id: string; reason: string; note: string } | null>(null);
  const [lead, setLead] = useState(false);
  const [dragOver, setDragOver] = useState('');
  const move = async (id: string, stage: string, extra: Record<string, string> = {}): Promise<void> => {
    if (stage === 'lost' && !extra.lostReason) { setLost({ id, reason: reasons[0] ?? 'other', note: '' }); return; }
    try {
      await api(`${T(gym.slug)}/admin/opportunities/${id}/stage`, { body: { stage, ...extra } });
      board.reload();
    } catch (e) { toast.error(e); }
  };
  const drop = (e: DragEvent<HTMLDivElement>, stage: string): void => {
    e.preventDefault();
    setDragOver('');
    const id = e.dataTransfer.getData('text/plain');
    if (id) void move(id, stage);
  };
  return (
    <div className="page wide">
      <PageHeader title={i18n.t('nav.pipeline')} subtitle={board.data ? i18n.t('crm.open_count', { count: board.data.openCount }) : undefined}
        actions={<>
          {gym.locations.length > 1 && <Select value={loc} onChange={(e) => setLoc(e.target.value)} placeholder={i18n.t('planner.all_locations')} options={gym.locations.map((l) => ({ value: l.id, label: l.name }))} />}
          <Button onClick={() => setLead(true)}>+ {i18n.t('crm.add_lead')}</Button>
        </>} />
      {board.error ? <ErrorBox error={board.error} onRetry={board.reload} /> : !board.data ? <Loading /> : (
        <div className="board">
          {board.data.columns.map((col: any) => (
            <div key={col.stage} className={cx('board-col', dragOver === col.stage && 'drag-over')}
              onDragOver={(e) => { e.preventDefault(); setDragOver(col.stage); }} onDragLeave={() => setDragOver('')} onDrop={(e) => drop(e, col.stage)}>
              <div className="board-head"><Badge value={col.stage} /> <span className="muted">{col.items.length}</span></div>
              {col.items.map((it: any) => (
                <div key={it.id} className="board-card" draggable onDragStart={(e) => e.dataTransfer.setData('text/plain', it.id)}>
                  <Link to={`${gym.base}/admin/clients/${it.personId}`}><strong>{it.name}</strong></Link>
                  <div className="small muted">{[it.source && i18n.option(it.source), it.campaign].filter(Boolean).join(' · ')}</div>
                  <div className="small muted">{i18n.date(it.stageChangedAt ?? it.createdAt)}{it.nextTaskAt ? ` · ⏰ ${i18n.dateTime(it.nextTaskAt)}` : ''}</div>
                  <select className="input input-sm" aria-label={i18n.t('crm.move_to')} value="" onChange={(e) => e.target.value && move(it.id, e.target.value)}>
                    <option value="">{i18n.t('crm.move_to')}…</option>
                    {board.data.columns.filter((c: any) => c.stage !== col.stage).map((c: any) => <option key={c.stage} value={c.stage}>{i18n.option(c.stage)}</option>)}
                  </select>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {lost && (
        <Modal title={i18n.t('crm.mark_lost')} onClose={() => setLost(null)} footer={
          <AsyncButton onClick={async () => { await move(lost.id, 'lost', { lostReason: lost.reason, lostNote: lost.note }); setLost(null); }}>{i18n.t('common.save')}</AsyncButton>
        }>
          <Field label={i18n.field('lost_reason')}><Select value={lost.reason} onChange={(e) => setLost({ ...lost, reason: e.target.value })} options={reasons.map((r) => ({ value: r, label: i18n.option(r) }))} /></Field>
          <Field label={i18n.field('lost_note')}><Textarea rows={2} value={lost.note} onChange={(e) => setLost({ ...lost, note: e.target.value })} /></Field>
        </Modal>
      )}
      {lead && <AddLeadModal onClose={() => setLead(false)} onSaved={board.reload} />}
    </div>
  );
}

export function InboxPage() {
  const gym = useGym();
  const i18n = useI18n();
  const { query } = useLocation();
  const [status, setStatus] = useState('handoff');
  const list = useApi(() => api<any[]>(`${T(gym.slug)}/admin/chat`, { query: { status } }), [status]);
  const selected = query.get('c');
  const conv = useApi(selected ? () => api(`${T(gym.slug)}/admin/chat/${selected}`) : null, [selected]);
  const [text, setText] = useState('');
  const [close, setClose] = useState(false);
  useInterval(() => { list.reload(); if (selected) conv.reload(); }, 10_000);
  useEffect(() => { setText(''); setClose(false); }, [selected]);
  return (
    <div className="page wide">
      <PageHeader title={i18n.t('nav.inbox')} actions={
        <Select value={status} onChange={(e) => setStatus(e.target.value)} options={['handoff', 'open', 'closed'].map((s) => ({ value: s, label: i18n.option(s) }))} />
      } />
      <div className="inbox">
        <div className="inbox-list">
          {!list.data ? <Loading /> : list.data.length === 0 ? <Empty icon="✓">{i18n.t('inbox.empty')}</Empty> : list.data.map((c) => (
            <button type="button" key={c.id} className={cx('inbox-item', selected === c.id && 'active')} onClick={() => setQuery({ c: c.id })}>
              <strong>{c.first_name ? `${c.first_name} ${c.last_name ?? ''}` : i18n.t('inbox.visitor')}</strong>
              <span className="small muted">{i18n.dateTime(c.last_message_at)} · {i18n.option(c.channel ?? 'web')}</span>
              <span className="small ellipsis">{c.last_message}</span>
            </button>
          ))}
        </div>
        <div className="inbox-thread">
          {!selected ? <Empty>{i18n.t('inbox.pick')}</Empty> : !conv.data ? <Loading /> : (
            <>
              <div className="chat-log">
                {conv.data.conversation.person_id && <Link to={`${gym.base}/admin/clients/${conv.data.conversation.person_id}`} className="small">{i18n.t('inbox.open_client')}</Link>}
                {conv.data.messages.map((m: any) => <div key={m.id} className={`msg msg-${m.role === 'staff' ? 'assistant staff' : m.role}`}><div className="pre">{m.content}</div><small className="muted">{i18n.time(m.created_at)}</small></div>)}
              </div>
              <div className="stack-sm">
                <Textarea rows={3} value={text} placeholder={i18n.t('inbox.reply_placeholder')} onChange={(e) => setText(e.target.value)} />
                <div className="row between">
                  <Checkbox checked={close} onChange={setClose} label={i18n.t('inbox.close_after')} />
                  <AsyncButton disabled={!text.trim() && !close} onClick={async () => { await api(`${T(gym.slug)}/admin/chat/${selected}/reply`, { body: { text, close } }); setText(''); conv.reload(); list.reload(); }}>{i18n.t('chat.send')}</AsyncButton>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function TasksPage() {
  const gym = useGym();
  const i18n = useI18n();
  const [status, setStatus] = useState('open');
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.tasks')} actions={<Select value={status} onChange={(e) => setStatus(e.target.value)} options={['open', 'done', 'cancelled'].map((s) => ({ value: s, label: i18n.option(s) }))} />} />
      <ResourcePage key={status} slug={gym.slug} resource="tasks" query={{ status }} columns={['title', 'person_id', 'due_at']}
        rowActions={(r, reload) => (r.status === 'open' ? <AsyncButton size="sm" variant="secondary" onClick={async () => { await api(`${T(gym.slug)}/admin/r/tasks/${r.id}`, { method: 'PATCH', body: { status: 'done' } }); reload(); }}>✓ {i18n.t('tasks.done')}</AsyncButton> : null)} />
    </div>
  );
}

